import React, { useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import TelegramWebApp from '../telegram-init';
import WebApp from '@twa-dev/sdk';
import { validateTelegramUser, getSubscriptionInfo } from '../services/api';
import { UserStoreContext, type UserStoreContextValue } from './UserStoreContext';

interface SubscriptionStatus {
  isActive: boolean;
  expiresAt: string | null;
  period: number;
  subscription_start_date: string | null;
}

interface User {
  name: string;
  lastName: string;
  avatar?: string;
  referralCode: string;
}

const getHighQualityAvatarUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  return `${url}?size=640`;
};

const initialUser: User = {
  name: TelegramWebApp.initDataUnsafe.user?.first_name || 'User',
  lastName: TelegramWebApp.initDataUnsafe.user?.last_name || '',
  avatar: getHighQualityAvatarUrl(TelegramWebApp.initDataUnsafe.user?.photo_url),
  referralCode: '',
};

const initialSubscriptionStatus: SubscriptionStatus = {
  isActive: false,
  expiresAt: '',
  period: 0,
  subscription_start_date: ''
};

interface UserStoreProviderProps {
  children: ReactNode;
}

export const UserStoreProvider: React.FC<UserStoreProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>(initialUser);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(initialSubscriptionStatus);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef(false);

  const loadUser = useCallback(async () => {
    try {
      const userData = await validateTelegramUser();
      setUser(prevUser => ({
        ...prevUser,
        name: userData.first_name || prevUser.name,
        lastName: userData.last_name || prevUser.lastName,
        avatar: getHighQualityAvatarUrl(TelegramWebApp.initDataUnsafe.user?.photo_url) || prevUser.avatar,
        referralCode: userData.referral_code || '',
      }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load user data');
      setError(error);
      console.error('[UserStore] Error loading user:', error);
    }
  }, []);

  const loadSubscription = useCallback(async () => {
    try {
      const subscriptionData = await getSubscriptionInfo();
      setSubscriptionStatus({
        isActive: subscriptionData.is_active,
        expiresAt: subscriptionData.subscription_expires_at,
        period: parseInt(subscriptionData.subscription_type?.split('_')[0] ?? '0'),
        subscription_start_date: subscriptionData.subscription_start_date
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load subscription data');
      setError(error);
      console.error('[UserStore] Error loading subscription:', error);
    }
  }, []);

  const reloadSubscription = useCallback(async () => {
    await loadSubscription();
  }, [loadSubscription]);

  const reloadUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (loadingRef.current) return;
      
      try {
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        
        if (!WebApp.initData) {
          throw new Error('WebApp not initialized');
        }

        if (!WebApp.isExpanded) {
          WebApp.ready();
          WebApp.setHeaderColor('secondary_bg_color');
        }

        await Promise.all([loadUser(), loadSubscription()]);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load profile data');
        setError(error);
        console.error('[UserStore] Error loading data:', error);
        TelegramWebApp.showPopup({
          title: 'Error',
          message: error.message,
          buttons: [{ type: 'default', text: 'OK' }]
        });
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadData();
  }, [loadUser, loadSubscription]);

  const value: UserStoreContextValue = {
    user,
    subscriptionStatus,
    loading,
    error,
    updateUser,
    reloadSubscription,
    reloadUser,
  };

  return (
    <UserStoreContext.Provider value={value}>
      {children}
    </UserStoreContext.Provider>
  );
};


